import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ObjectApiInterface } from '../base/interfaces/object-api.interface';
import {
  PurchaseActorAssignPayload,
  PurchaseActorConfiguredNodesResponse,
  PurchaseActorDependencies,
  PurchaseActorReplaceContactPayload,
  PurchaseActorResolveResponse,
  PurchaseActorUnassignPayload,
} from '../models/purchase-actor.model';

/**
 * Configuration des acteurs achat (PRED-3139) : résolution en cascade (6 niveaux),
 * affectation par nœud (tenant/établissement + catégorie), impacts et remplacement
 * d'un contact référencé. Calque de `DefaultSupervisionService`.
 */
@Injectable({
  providedIn: 'root',
})
export class PurchaseActorService {
  baseUrlApi = '/api/purchase-actor';

  constructor(public http: HttpClient) {}

  /**
   * Résolution effective (cascade) d'un point (établissement/catégorie) donné, ET la
   * config explicite (non résolue) de ce même nœud exact.
   */
  resolve(categoryId?: string, establishmentId?: string): Observable<ObjectApiInterface<PurchaseActorResolveResponse>> {
    let params = new HttpParams();
    if (categoryId) {
      params = params.set('categoryId', categoryId);
    }
    if (establishmentId) {
      params = params.set('establishmentId', establishmentId);
    }
    return this.http.get<ObjectApiInterface<PurchaseActorResolveResponse>>(`${this.baseUrlApi}/resolve`, { params });
  }

  /**
   * Ids des catégories ayant une config explicite dans le scope demandé + indicateur de
   * présence d'un « défaut » (doc `category: null`).
   */
  configuredNodes(establishmentId?: string): Observable<ObjectApiInterface<PurchaseActorConfiguredNodesResponse>> {
    let params = new HttpParams();
    if (establishmentId) {
      params = params.set('establishmentId', establishmentId);
    }
    return this.http.get<ObjectApiInterface<PurchaseActorConfiguredNodesResponse>>(
      `${this.baseUrlApi}/configured-nodes`,
      { params },
    );
  }

  /** Écrit (upsert) la configuration explicite d'un nœud — sémantique plein remplacement. */
  assign(payload: PurchaseActorAssignPayload): Observable<ObjectApiInterface<any>> {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/assign`, payload);
  }

  /** Suppression physique de la configuration explicite d'un nœud (tous rôles confondus). */
  unassign(payload: PurchaseActorUnassignPayload): Observable<ObjectApiInterface<any>> {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/unassign`, payload);
  }

  /** Impacts d'un user-profile référencé (dans n'importe quel rôle) au sein de la configuration. */
  checkContactDependencies(
    userId: string,
    establishmentId?: string,
  ): Observable<ObjectApiInterface<PurchaseActorDependencies>> {
    let params = new HttpParams();
    if (establishmentId) {
      params = params.set('establishmentId', establishmentId);
    }
    return this.http.post<ObjectApiInterface<PurchaseActorDependencies>>(
      `${this.baseUrlApi}/checkContactDependencies/${userId}`,
      {},
      { params },
    );
  }

  /** Remplacement (ou retrait pur si `newContact` absent) transactionnel d'un contact référencé. */
  replaceContact(payload: PurchaseActorReplaceContactPayload): Observable<ObjectApiInterface<any>> {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/replaceContact`, payload);
  }
}
